import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, X } from 'lucide-react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  meta?: string;
  keywords?: string[];
  data?: unknown;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  pinnedOptions?: SearchableSelectOption[];
  value: string;
  onValueChange: (value: string, option: SearchableSelectOption) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: (query: string) => React.ReactNode;
  filterOptions?: (options: SearchableSelectOption[], query: string) => SearchableSelectOption[];
  onSearchChange?: (query: string) => void;
  loading?: boolean;
  disabled?: boolean;
  modal?: boolean;
  triggerClassName?: string;
  listClassName?: string;
  triggerTestId?: string;
  searchTestId?: string;
}

const defaultFilter = (
  options: SearchableSelectOption[],
  query: string
): SearchableSelectOption[] => {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) =>
    [o.label, ...(o.keywords || [])].some((s) =>
      String(s || '').toLowerCase().includes(q)
    )
  );
};

export function SearchableSelect({
  options,
  pinnedOptions = [],
  value,
  onValueChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage,
  filterOptions,
  onSearchChange,
  loading = false,
  disabled = false,
  modal = true,
  triggerClassName,
  listClassName,
  triggerTestId,
  searchTestId,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const filtered = React.useMemo(
    () =>
      onSearchChange ? options : (filterOptions || defaultFilter)(options, search),
    [onSearchChange, filterOptions, options, search]
  );

  const selected =
    options.find((o) => o.value === value) ||
    pinnedOptions.find((o) => o.value === value);

  const emptyNode =
    filtered.length === 0 && !loading ? emptyMessage?.(search.trim()) : null;

  const handleSearchChange = (next: string) => {
    setSearch(next);
    onSearchChange?.(next);
  };

  const handleSelect = (option: SearchableSelectOption) => {
    onValueChange(option.value, option);
    setSearch('');
    setOpen(false);
  };

  const renderItem = (option: SearchableSelectOption) => {
    const isActive = option.value === value;
    return (
      <CommandItem
        key={option.value}
        value={option.value}
        onSelect={() => handleSelect(option)}
        className={cn(
          'relative cursor-pointer rounded-md py-2 pl-8 pr-2 text-[13px] text-[#101828]',
          'data-[selected=true]:bg-[#F9FAFB] data-[selected=true]:text-[#101828]',
          isActive && 'bg-brand-subtle text-brand-cta data-[selected=true]:bg-brand-subtle data-[selected=true]:text-brand-cta',
        )}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check
            className={cn(
              'h-3.5 w-3.5 text-brand',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
        </span>
        {(() => {
          const body = option.description ? (
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{option.label}</span>
              <span className="truncate text-[10px] leading-tight text-[#98A2B3]">
                {option.description}
              </span>
            </div>
          ) : (
            <span className="truncate">{option.label}</span>
          );
          if (!option.meta) return body;
          return (
            <div className="flex w-full min-w-0 items-center gap-2">
              <div className="min-w-0 flex-1">{body}</div>
              <span className="shrink-0 text-[11px] text-[#98A2B3]">{option.meta}</span>
            </div>
          );
        })()}
      </CommandItem>
    );
  };

  return (
    <Popover
      modal={modal}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) handleSearchChange('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={triggerTestId}
          data-state={open ? 'open' : 'closed'}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-lg border border-[#E4E7EC] bg-white px-2.5 py-2 text-[13px] text-[#101828] transition',
            'hover:border-[#D0D5DD]',
            'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15',
            'data-[state=open]:border-brand data-[state=open]:ring-2 data-[state=open]:ring-brand/15',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !selected && 'text-[#98A2B3]',
            triggerClassName,
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {selected?.label || placeholder}
          </span>
          <ChevronDown
            className={cn(
              'ml-2 h-3.5 w-3.5 shrink-0 text-[#98A2B3] transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] overflow-hidden rounded-xl border border-[#E4E7EC] bg-white p-0 text-[#101828] shadow-[0_8px_20px_rgba(16,24,40,0.08)]"
        align="start"
      >
        <Command shouldFilter={false} className="rounded-xl bg-white">
          <div className="relative">
            <CommandInput
              ref={searchInputRef}
              data-testid={searchTestId}
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={handleSearchChange}
              className="h-10 pr-8 text-[13px] text-[#101828] placeholder:text-[#98A2B3]"
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                data-testid={searchTestId ? `${searchTestId}-clear` : undefined}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-[#98A2B3] opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-brand/30"
                onClick={() => {
                  handleSearchChange('');
                  searchInputRef.current?.focus();
                }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <CommandList className={cn('max-h-[240px] p-1', listClassName)}>
            {(pinnedOptions.length > 0 || filtered.length > 0) && (
              <CommandGroup className="p-0">
                {pinnedOptions.map(renderItem)}
                {filtered.map(renderItem)}
              </CommandGroup>
            )}
            {loading && (
              <div className="px-2 py-3 text-center text-[12px] text-[#98A2B3]">
                Searching…
              </div>
            )}
            {emptyNode != null && emptyNode !== false && (
              <div className="px-2 py-3 text-center text-[12px] text-[#98A2B3]">
                {emptyNode}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
